import type { AxiosInstance } from 'axios';
import type { AuthService } from 'types/AuthService';
import type { InvoiceDataUseCases } from '@subscriptions/composition/types';
import { createInvoiceDataHttpClient } from '@subscriptions/infrastructure/api/InvoiceDataHttpClient';
import { createCsfHttpClient } from '@subscriptions/infrastructure/api/CsfHttpClient';
import { createInvoiceDataApiMapper } from '@subscriptions/infrastructure/mappers/InvoiceDataApiMapper';
import { createCsfApiMapper } from '@subscriptions/infrastructure/mappers/CsfApiMapper';
import { createInvoiceDataApiRepository } from '@subscriptions/infrastructure/repositories/InvoiceDataApiRepository';
import { createCsfApiRepository } from '@subscriptions/infrastructure/repositories/CsfApiRepository';
import {
  createInvoiceDataUseCase,
  updateInvoiceDataUseCase,
  retryInvoiceDataUseCase,
  listInvoiceDataUseCase,
  getInvoiceDataUseCase,
  deleteInvoiceDataUseCase,
  listFiscalRegimesUseCase,
  extractCsfDataUseCase,
} from '@subscriptions/application/use-cases';

/**
 * Assembles all invoice data use cases with their dependencies.
 *
 * @param httpClientSubscription - Axios bound to parrot-subscriptions-api (CRUD)
 * @param httpClientApi - Axios bound to the legacy backend (fiscal regimes)
 * @param httpClientOnboarding - Axios bound to parrot-onboarding-api (CSF extraction)
 * @param authService - Authentication service (used by legacy regime lookup)
 */
export const createInvoiceDataUseCases = (
  httpClientSubscription: AxiosInstance,
  httpClientApi: AxiosInstance,
  httpClientOnboarding: AxiosInstance,
  authService: AuthService,
): InvoiceDataUseCases => {
  const invoiceDataHttpClient = createInvoiceDataHttpClient({ httpClientSubscription, httpClientApi, authService });
  const mapper = createInvoiceDataApiMapper();
  const repository = createInvoiceDataApiRepository({ httpClient: invoiceDataHttpClient, mapper });
  const csfRepository = createCsfApiRepository({
    httpClient: createCsfHttpClient({
      httpClient: httpClientOnboarding,
      getStoreUuid: () => authService?.store?.uuid,
    }),
    mapper: createCsfApiMapper(),
  });

  return {
    createInvoiceData: createInvoiceDataUseCase(repository),
    updateInvoiceData: updateInvoiceDataUseCase(repository),
    retryInvoiceData: retryInvoiceDataUseCase(repository),
    listInvoiceData: listInvoiceDataUseCase(repository),
    getInvoiceData: getInvoiceDataUseCase(repository),
    deleteInvoiceData: deleteInvoiceDataUseCase(repository),
    listFiscalRegimes: listFiscalRegimesUseCase(repository),
    extractCsfData: extractCsfDataUseCase(csfRepository),
  };
};
